document.addEventListener('DOMContentLoaded', () => {
  const dialog = document.getElementById('global-popup-dialog');
  if (!dialog) return;

  const bodyContainer = dialog.querySelector('.popup-dialog__body');
  const closeBtn = dialog.querySelector('.popup-dialog__close');
  let isLoading = false;

  const contentCache = new Map();
  const parser = new DOMParser();
  const preloadQueue = new Set();
  const preloadDelay = 200; // 鼠标悬停200ms后开始预加载

  let scrollTop = 0;

  // 锁定背景滚动（用 overflow:hidden + padding 补偿）
  const lockBodyScroll = () => {
    scrollTop = window.scrollY;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  };

  // 解锁背景滚动
  const unlockBodyScroll = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollTop);
  };

  // 加载并缓存内容
  const loadContent = (url) => {
    if (contentCache.has(url)) {
      return Promise.resolve(contentCache.get(url));
    }

    if (preloadQueue.has(url)) {
      // 如果正在预加载，等待预加载完成
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (contentCache.has(url)) {
            clearInterval(checkInterval);
            resolve(contentCache.get(url));
          } else if (!preloadQueue.has(url)) {
            clearInterval(checkInterval);
            // 预加载失败，重新加载
            loadContentInternal(url).then(resolve).catch(reject);
          }
        }, 50);
      });
    }

    return loadContentInternal(url);
  };

  // 内部加载函数
  const loadContentInternal = (url) => {
    return fetch(url)
      .then(res => res.text())
      .then(html => {
        const doc = parser.parseFromString(html, 'text/html');
        const mainContent = doc.querySelector('main') || doc.body;
        const content = mainContent?.innerHTML || 'loading failed';
        contentCache.set(url, content);
        return content;
      });
  };

  // 预加载内容
  const preloadContent = (url) => {
    if (contentCache.has(url) || preloadQueue.has(url) || !url) return;

    preloadQueue.add(url);
    loadContentInternal(url)
      .catch(() => {
        // 预加载失败不影响，静默处理
      })
      .finally(() => {
        preloadQueue.delete(url);
      });
  };

  if (!window.popupLinkBound) {
    window.popupLinkBound = true;

    // 预加载处理函数
    const handlePreload = (link) => {
      const { openType = 'dialog' } = link.dataset;
      if (openType !== 'dialog') return;

      const url = link.getAttribute('href');
      if (url && !contentCache.has(url) && !preloadQueue.has(url)) {
        preloadContent(url);
      }
    };

    // 鼠标悬停预加载（延迟，避免快速划过时不必要的请求）
    const hoverTimeouts = new WeakMap();
    let currentHoveredLink = null;
    
    // 使用事件委托处理鼠标悬停
    document.addEventListener('mouseover', (e) => {
      const link = e.target?.closest('.popup_link');
      
      // 如果移到了新的链接上
      if (link && link !== currentHoveredLink) {
        // 清除之前链接的定时器
        if (currentHoveredLink) {
          const prevTimeout = hoverTimeouts.get(currentHoveredLink);
          if (prevTimeout) {
            clearTimeout(prevTimeout);
            hoverTimeouts.delete(currentHoveredLink);
          }
        }
        
        currentHoveredLink = link;
        
        // 延迟预加载
        const timeoutId = setTimeout(() => {
          handlePreload(link);
          hoverTimeouts.delete(link);
        }, preloadDelay);
        
        hoverTimeouts.set(link, timeoutId);
      }
      
      // 如果移出了所有链接区域
      if (!link && currentHoveredLink) {
        const timeoutId = hoverTimeouts.get(currentHoveredLink);
        if (timeoutId) {
          clearTimeout(timeoutId);
          hoverTimeouts.delete(currentHoveredLink);
        }
        currentHoveredLink = null;
      }
    });

    // 鼠标按下时立即预加载（作为备用，确保点击时有内容）
    document.addEventListener('mousedown', (e) => {
      const link = e.target.closest('.popup_link');
      if (link) {
        handlePreload(link);
      }
    });

    // 点击事件处理
    document.addEventListener('click', e => {
      const link = e.target.closest('.popup_link');
      if (!link) return;

      e.preventDefault();
      const { openType = 'dialog' } = link.dataset;
      const url = link.getAttribute('href');
      if (!url) return;

      if (openType === 'blank') {
        window.open(url, '_blank', 'noopener');
        return;
      }
      if (openType === 'inner') {
        location.href = url;
        return;
      }

      if (isLoading || dialog.open) return;

      // 如果有缓存，先设置内容再显示对话框，可能更快
      const cachedContent = contentCache.get(url);
      if (cachedContent && bodyContainer) {
        bodyContainer.innerHTML = cachedContent;
      } else if (bodyContainer) {
        bodyContainer.innerHTML = `
          <div class="popup-loading">
            <div class="popup-loading__spinner"></div>
          </div>
        `;
      }

      // 立即显示对话框（关键操作，必须同步）
      // 这是最耗时的操作，但必须同步执行
      dialog.showModal();
      
      // 添加 show 类（同步，但很快）
      dialog.classList.add('show');
      
      // 锁定背景滚动（同步执行，操作很快）
      lockBodyScroll();

      // 如果有缓存，直接返回
      if (cachedContent) {
        return;
      }

      isLoading = true;

      loadContent(url)
        .then(content => {
          if (bodyContainer) {
            bodyContainer.innerHTML = content;
          }
        })
        .catch(() => {
          if (bodyContainer) {
            bodyContainer.textContent = 'Loading failed, please try again later.';
          }
        })
        .finally(() => {
          isLoading = false;
        });
    });
  }

  const closePopup = () => {
    dialog.classList.remove('show');

    // 👉 清除焦点，避免页面跳动
    if (document.activeElement) {
      document.activeElement.blur();
    }

    setTimeout(() => {
      if (dialog.open) dialog.close();
      bodyContainer.scrollTop = 0;

      requestAnimationFrame(() => {
        unlockBodyScroll();
      });
    }, 250);
  };

  closeBtn.addEventListener('click', closePopup);
  dialog.addEventListener('click', e => {
    if (e.target === dialog) closePopup();
  });
  dialog.addEventListener('cancel', e => {
    e.preventDefault();
    closePopup();
  });
});
